import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import io from 'socket.io-client';

// Sem interação real (mouse/teclado/toque) por este tempo, a conexão em tempo
// real é considerada ociosa e desconectada — ela volta sozinha na próxima
// interação. Isso evita que uma aba aberta e esquecida mantenha o usuário
// "online" indefinidamente (ver onlineUsersService/dashboardStatsCache no
// backend, que fazem polling enquanto houver alguém online).
const IDLE_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutos
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'];

/**
 * ✅ Hook Global de Socket.IO com Debounce
 * Centraliza a lógica de atualização em tempo real para TODAS as páginas
 * Evita requisições excessivas (429 - Too Many Requests)
 *
 * Uso: Adicionar no componente raiz (App.jsx)
 *
 * @example
 * function App() {
 *   useGlobalSocket(); // ← Adicionar esta linha
 *   return <Router>...</Router>
 * }
 */
export function useGlobalSocket() {
  const queryClient = useQueryClient();
  const invalidationTimers = useRef({}); // Timers para debounce

  useEffect(() => {
    // Conectar ao servidor Socket.IO
    // ✅ Em produção: usa mesma URL do frontend (Nginx faz proxy)
    // ✅ Em desenvolvimento: usa porta 3010 diretamente
    const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const serverUrl = isDevelopment 
      ? `${window.location.protocol}//${window.location.hostname}:3010`
      : window.location.origin; // Usa mesma origem (Nginx faz proxy)
    
    const socket = io(serverUrl, {
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      path: '/socket.io', // ✅ Caminho do Socket.IO (Nginx faz proxy)
    });

    // Eventos de conexão
    socket.on('connect', () => {
      // Socket conectado
    });

    socket.on('disconnect', () => {
      // Socket desconectado
    });

    // ✅ Desconexão por inatividade real (não apenas aba aberta sem uso)
    let idleTimer = null;
    let idledOut = false;

    const scheduleIdleDisconnect = () => {
      if (idleTimer) clearTimeout(idleTimer);
      idleTimer = setTimeout(() => {
        idledOut = true;
        socket.disconnect();
      }, IDLE_TIMEOUT_MS);
    };

    const handleActivity = () => {
      if (idledOut) {
        idledOut = false;
        socket.connect(); // reconecta na primeira interação depois da ociosidade
      }
      scheduleIdleDisconnect();
    };

    ACTIVITY_EVENTS.forEach(evt => window.addEventListener(evt, handleActivity, { passive: true }));
    scheduleIdleDisconnect();

    // ✅ EVENTO PRINCIPAL: data_updated com DEBOUNCE
    // Escuta TODOS os tipos de atualização de dados
    // Agrupa invalidações para evitar requisições excessivas
    socket.on('data_updated', (update) => {

      // Mapeamento de tipos de evento para queries a invalidar
      const invalidationMap = {
        // Parcerias
        'partnership_created': ['partnerships', 'osc-partnerships', 'dashboard-stats'],
        'partnership_updated': ['partnerships', 'osc-partnerships', 'dashboard-stats'],
        'partnership_deleted': ['partnerships', 'osc-partnerships', 'dashboard-stats'],
        'partnership_imported': ['partnerships', 'osc-partnerships', 'dashboard-stats'],
        
        // Usuários
        'user_created': ['users', 'user-stats'],
        'user_updated': ['users', 'user-stats'],
        'user_deleted': ['users', 'user-stats'],
        'user_status_changed': ['users', 'user-stats'],
        
        // Auditoria
        'audit_log_created': ['audit-logs'],
        
        // Genérico (invalida tudo)
        'data_changed': ['partnerships', 'users', 'audit-logs', 'dashboard-stats'],
      };

      // Obter queries a invalidar
      const queriesToInvalidate = invalidationMap[update.type] || [];

      // ✅ DEBOUNCE: Agrupar invalidações em 1 segundo
      // Evita múltiplas invalidações simultâneas (429 - Too Many Requests)
      queriesToInvalidate.forEach(queryKey => {
        // Limpar timer anterior se existir
        if (invalidationTimers.current[queryKey]) {
          clearTimeout(invalidationTimers.current[queryKey]);
        }

        // Criar novo timer
        invalidationTimers.current[queryKey] = setTimeout(() => {
          queryClient.invalidateQueries([queryKey]);
          delete invalidationTimers.current[queryKey];
        }, 1000); // 1 segundo de debounce
      });

      // Se não houver mapeamento específico, invalidar tudo (fallback) - SEM debounce
      if (queriesToInvalidate.length === 0) {
        queryClient.invalidateQueries();
      }
    });

    // ✅ EVENTO: import_progress (progresso de importação)
    socket.on('import_progress', (progress) => {
      // Este evento é tratado localmente pelo componente de importação
      // Não precisa invalidar cache aqui
    });

    // Cleanup ao desmontar
    return () => {

      // Limpar todos os timers pendentes
      Object.values(invalidationTimers.current).forEach(timer => {
        clearTimeout(timer);
      });
      invalidationTimers.current = {};

      ACTIVITY_EVENTS.forEach(evt => window.removeEventListener(evt, handleActivity));
      if (idleTimer) clearTimeout(idleTimer);

      socket.disconnect();
    };
  }, [queryClient]);
}

export default useGlobalSocket;
